import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    customizations: {},
    showCustomization: true,
    dragging: null,
    resizing: null,
    selectedElement: null,
    currentProductId: null,
    currentPosition: 'front',
    uploadedFiles: {},
    previewImages: {},
    history: {
        past: [],
        present: null,
        future: [],
        limit: 50
    }
};

const getDefaultCustomization = () => ({
    logo: { x: 50, y: 25 },
    name: { x: 50, y: 50 },
    number: { x: 50, y: 75 },
    
    logoPreview: null,
    customerName: '',
    playerNumber: '',
    
    logoSize: { width: 60, height: 60 },
    nameSize: { width: 120, height: 48 },
    numberSize: { width: 60, height: 60 },
    
    nameColor: '#000000',
    numberColor: '#000000',
    
    nameFontFamily: 'Arial',
    numberFontFamily: 'Arial',
    
    nameStyles: {
        bold: false,
        italic: false,
        uppercase: false,
        underline: false,
        strikethrough: false
    },
    
    numberStyles: {
        bold: false,
        italic: false,
        uppercase: false,
        underline: false,
        strikethrough: false,
        circleBackground: false,
        numberBackgroundColor: '#f0f0f0',
        numberBorderColor: '#cccccc'
    },
    
    logoRotation: 0,
    nameRotation: 0,
    numberRotation: 0,
    
    logoOpacity: 1,
    nameOpacity: 1,
    numberOpacity: 1,
    
    logoScaleX: 1,
    logoScaleY: 1,
    nameScaleX: 1,
    nameScaleY: 1,
    numberScaleX: 1,
    numberScaleY: 1,
    
    logoLocked: false,
    nameLocked: false,
    numberLocked: false,
    
    logoBlendMode: 'normal',
    nameBlendMode: 'normal',
    numberBlendMode: 'normal',
    
    nameEffects: {
        shadowEnabled: false,
        shadowColor: '#000000',
        shadowBlur: 2,
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        outlineEnabled: false,
        outlineColor: '#FFFFFF',
        outlineWidth: 2,
        glowEnabled: false,
        glowColor: '#FF0000',
        glowBlur: 10
    },
    
    numberEffects: {
        shadowEnabled: false,
        shadowColor: '#000000',
        shadowBlur: 2,
        shadowOffsetX: 1,
        shadowOffsetY: 1,
        outlineEnabled: false,
        outlineColor: '#FFFFFF',
        outlineWidth: 2,
        glowEnabled: false,
        glowColor: '#FF0000',
        glowBlur: 10
    },
    
    nameCurve: {
        enabled: false,
        radius: 30,
        direction: 'up',
        textSpacing: 5,
        curveStyle: 'arc'
    },
    
    numberCurve: {
        enabled: false,
        radius: 30,
        direction: 'up',
        textSpacing: 5,
        curveStyle: 'arc'
    },
    
    nameAlignment: {
        horizontal: 'center',
        vertical: 'middle'
    },
    
    numberAlignment: {
        horizontal: 'center',
        vertical: 'middle'
    },
    
    nameSpacing: {
        letterSpacing: 0,
        lineHeight: 1,
        padding: 0
    },
    
    numberSpacing: {
        letterSpacing: 0,
        lineHeight: 1,
        padding: 0
    },
    
    logoFilters: {
        grayscale: 0,
        sepia: 0,
        brightness: 100,
        contrast: 100,
        saturate: 100,
        hueRotate: 0,
        blur: 0,
        invert: 0
    },
    
    nameFilters: {
        grayscale: 0,
        sepia: 0,
        brightness: 100,
        contrast: 100,
        saturate: 100,
        hueRotate: 0,
        blur: 0,
        invert: 0
    },
    
    numberFilters: {
        grayscale: 0,
        sepia: 0,
        brightness: 100,
        contrast: 100,
        saturate: 100,
        hueRotate: 0,
        blur: 0,
        invert: 0
    },
    
    logoBorder: {
        enabled: false,
        color: '#000000',
        width: 1,
        style: 'solid'
    },
    
    nameBorder: {
        enabled: false,
        color: '#000000',
        width: 1,
        style: 'solid'
    },
    
    numberBorder: {
        enabled: false,
        color: '#000000',
        width: 1,
        style: 'solid'
    },
    
    _originalLogoFile: null
});

const customizationSlice = createSlice({
    name: 'customization',
    initialState,
    reducers: {
        setCustomizationPosition: (state, action) => {
            const { imageIndex, type, position } = action.payload;

            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }

            const clampedPosition = {
                x: Math.max(1, Math.min(99, position.x)),
                y: Math.max(1, Math.min(99, position.y))
            };

            state.customizations[imageIndex][type] = clampedPosition;
        },

        setElementBorder: (state, action) => {
            const { imageIndex, elementType, border } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Border`] = border;
        },

        setElementFilter: (state, action) => {
            const { imageIndex, elementType, filter, value } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            
            const filterField = `${elementType}Filters`;
            if (!state.customizations[imageIndex][filterField]) {
                state.customizations[imageIndex][filterField] = getDefaultCustomization()[filterField];
            }
            
            state.customizations[imageIndex][filterField][filter] = value;
        },

        setBlendMode: (state, action) => {
            const { imageIndex, elementType, blendMode } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}BlendMode`] = blendMode;
        },

        setElementLock: (state, action) => {
            const { imageIndex, elementType, locked } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Locked`] = locked;
        },

        setSpacing: (state, action) => {
            const { imageIndex, elementType, spacing } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Spacing`] = spacing;
        },

        setAlignment: (state, action) => {
            const { imageIndex, elementType, alignment } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Alignment`] = alignment;
        },

        setCurveSettings: (state, action) => {
            const { imageIndex, elementType, settings } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Curve`] = settings;
        },

        setTextEffects: (state, action) => {
            const { imageIndex, elementType, effects } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Effects`] = effects;
        },

        setElementRotation: (state, action) => {
            const { imageIndex, type, rotation } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${type}Rotation`] = rotation;
        },

        setElementOpacity: (state, action) => {
            const { imageIndex, type, opacity } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${type}Opacity`] = opacity;
        },

        setDragging: (state, action) => {
            state.dragging = action.payload;
        },

        setResizing: (state, action) => {
            state.resizing = action.payload;
        },

        setSelectedElement: (state, action) => {
            state.selectedElement = action.payload;
        },

        resizeElement: (state, action) => {
            const { imageIndex, type, size } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${type}Size`] = size;
        },

        setNameStyles: (state, action) => {
            const { imageIndex, styles } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex].nameStyles = {
                ...(state.customizations[imageIndex].nameStyles || {}),
                ...styles
            };
        },

        setNumberStyles: (state, action) => {
            const { imageIndex, styles } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex].numberStyles = {
                ...(state.customizations[imageIndex].numberStyles || {}),
                ...styles
            };
        },

        setLogoStyles: (state, action) => {
            const { imageIndex, styles } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex].logoStyles = {
                ...(state.customizations[imageIndex].logoStyles || {}),
                ...styles
            };
        },

        setCustomizationData: (state, action) => {
            const { imageIndex, field, value } = action.payload;
            
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }

            if (Array.isArray(field) && Array.isArray(value)) {
                field.forEach((fieldName, index) => {
                    if (typeof value[index] === 'object' && value[index] !== null) {
                        state.customizations[imageIndex][fieldName] = {
                            ...(state.customizations[imageIndex][fieldName] || {}),
                            ...value[index]
                        };
                    } else {
                        state.customizations[imageIndex][fieldName] = value[index];
                    }
                });
            } else if (typeof field === 'object' && field !== null) {
                Object.keys(field).forEach(key => {
                    if (typeof field[key] === 'object' && field[key] !== null) {
                        state.customizations[imageIndex][key] = {
                            ...(state.customizations[imageIndex][key] || {}),
                            ...field[key]
                        };
                    } else {
                        state.customizations[imageIndex][key] = field[key];
                    }
                });
            } else if (typeof value === 'object' && value !== null) {
                state.customizations[imageIndex][field] = {
                    ...(state.customizations[imageIndex][field] || {}),
                    ...value
                };
            } else {
                state.customizations[imageIndex][field] = value;
            }
            
            state.showCustomization = true;
        },

        setUploadedFile: (state, action) => {
            const { imageIndex, file } = action.payload;
            if (file) {
                state.uploadedFiles[imageIndex] = {
                    name: file.name,
                    type: file.type,
                    size: file.size,
                    lastModified: file.lastModified,
                    fileObject: file,
                    _isLogo: true,
                    _imageIndex: imageIndex
                };
            }
        },

        setPreviewImage: (state, action) => {
            const { imageIndex, previewDataUrl } = action.payload;
            state.previewImages[imageIndex] = previewDataUrl;
        },

        clearUploadedFile: (state, action) => {
            const { imageIndex } = action.payload;
            delete state.uploadedFiles[imageIndex];
            delete state.previewImages[imageIndex];
            if (state.customizations[imageIndex]) {
                state.customizations[imageIndex].logoPreview = null;
                state.customizations[imageIndex]._originalLogoFile = null;
            }
        },

        initializeImageCustomization: (state, action) => {
            const { imageIndex } = action.payload;

            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
        },

        toggleCustomizationVisibility: (state) => {
            state.showCustomization = !state.showCustomization;
        },

        resetCustomizations: (state) => {
            state.customizations = {};
            state.uploadedFiles = {};
            state.previewImages = {};
            state.showCustomization = false;
            state.dragging = null;
            state.resizing = null;
            state.selectedElement = null;
            state.history.past = [];
            state.history.present = null;
            state.history.future = [];
        },

        resetImageCustomization: (state, action) => {
            const { imageIndex } = action.payload;
            if (state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            delete state.uploadedFiles[imageIndex];
            delete state.previewImages[imageIndex];
        },

        resetProductCustomizations: (state, action) => {
            const productId = action.payload;
            if (!productId || state.currentProductId === productId) {
                state.customizations = {};
                state.uploadedFiles = {};
                state.previewImages = {};
                state.showCustomization = false;
                state.dragging = null;
                state.resizing = null;
                state.selectedElement = null;
            }
        },

        setCurrentProduct: (state, action) => {
            state.currentProductId = action.payload;
        },

        setCurrentPosition: (state, action) => {
            state.currentPosition = action.payload;
        },

        saveCustomizationsFromCustomizePage: (state, action) => {
            const { customizations, uploadedFiles, previewImages } = action.payload;

            state.customizations = {};
            state.uploadedFiles = {};
            state.previewImages = {};

            if (customizations) {
                Object.keys(customizations).forEach(imageIndex => {
                    const existingDefault = getDefaultCustomization();
                    const incomingCustomization = customizations[imageIndex] || {};

                    state.customizations[imageIndex] = {
                        ...existingDefault,
                        ...incomingCustomization
                    };
                });
            }

            if (uploadedFiles) {
                Object.keys(uploadedFiles).forEach(key => {
                    state.uploadedFiles[key] = uploadedFiles[key];
                });
            }

            if (previewImages) {
                Object.keys(previewImages).forEach(key => {
                    state.previewImages[key] = previewImages[key];
                });
            }

            state.showCustomization = true;

            const snapshot = {
                customizations: JSON.parse(JSON.stringify(state.customizations)),
                uploadedFiles: JSON.parse(JSON.stringify(state.uploadedFiles)),
                previewImages: JSON.parse(JSON.stringify(state.previewImages)),
                timestamp: Date.now()
            };

            if (state.history.present) {
                if (state.history.past.length >= state.history.limit) {
                    state.history.past.shift();
                }
                state.history.past.push(state.history.present);
            }
            state.history.present = snapshot;
            state.history.future = [];
        },

        saveToHistory: (state, action) => {
            const snapshot = action.payload;

            if (state.history.present) {
                if (state.history.past.length >= state.history.limit) {
                    state.history.past.shift();
                }
                state.history.past.push(state.history.present);
            }
            state.history.present = snapshot;
            state.history.future = [];
        },

        undo: (state) => {
            if (state.history.past.length > 0) {
                if (state.history.present) {
                    state.history.future.unshift(state.history.present);
                }
                const previous = state.history.past.pop();
                state.history.present = previous;
                state.customizations = previous.customizations;
                state.uploadedFiles = previous.uploadedFiles;
                state.previewImages = previous.previewImages;
            }
        },

        redo: (state) => {
            if (state.history.future.length > 0) {
                if (state.history.present) {
                    state.history.past.push(state.history.present);
                }
                const next = state.history.future.shift();
                state.history.present = next;
                state.customizations = next.customizations;
                state.uploadedFiles = next.uploadedFiles;
                state.previewImages = next.previewImages;
            }
        },

        clearHistory: (state) => {
            state.history.past = [];
            state.history.present = null;
            state.history.future = [];
        },

        setLogoSize: (state, action) => {
            const { imageIndex, size } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex].logoSize = size;
        },

        setNameColor: (state, action) => {
            const { imageIndex, color } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex].nameColor = color;
        },

        setNumberColor: (state, action) => {
            const { imageIndex, color } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex].numberColor = color;
        },

        setFontFamily: (state, action) => {
            const { imageIndex, fontFamily } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex].fontFamily = fontFamily;
        },

        setTextStyle: (state, action) => {
            const { imageIndex, style, value } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            if (!state.customizations[imageIndex].textStyles) {
                state.customizations[imageIndex].textStyles = {};
            }
            state.customizations[imageIndex].textStyles[style] = value;
        },

        setTextCurve: (state, action) => {
            const { imageIndex, elementType, curveSettings } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Curve`] = curveSettings;
        },

        setTextShadow: (state, action) => {
            const { imageIndex, elementType, shadow } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            if (!state.customizations[imageIndex][`${elementType}Effects`]) {
                state.customizations[imageIndex][`${elementType}Effects`] = getDefaultCustomization()[`${elementType}Effects`];
            }
            state.customizations[imageIndex][`${elementType}Effects`].shadowEnabled = shadow.enabled;
            state.customizations[imageIndex][`${elementType}Effects`].shadowColor = shadow.color;
            state.customizations[imageIndex][`${elementType}Effects`].shadowBlur = shadow.blur;
            state.customizations[imageIndex][`${elementType}Effects`].shadowOffsetX = shadow.offsetX;
            state.customizations[imageIndex][`${elementType}Effects`].shadowOffsetY = shadow.offsetY;
        },

        setTextAlignment: (state, action) => {
            const { imageIndex, elementType, alignment } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            state.customizations[imageIndex][`${elementType}Alignment`] = alignment;
        },

        setLetterSpacing: (state, action) => {
            const { imageIndex, elementType, letterSpacing } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            if (!state.customizations[imageIndex][`${elementType}Spacing`]) {
                state.customizations[imageIndex][`${elementType}Spacing`] = getDefaultCustomization()[`${elementType}Spacing`];
            }
            state.customizations[imageIndex][`${elementType}Spacing`].letterSpacing = letterSpacing;
        },

        setLineHeight: (state, action) => {
            const { imageIndex, elementType, lineHeight } = action.payload;
            if (!state.customizations[imageIndex]) {
                state.customizations[imageIndex] = getDefaultCustomization();
            }
            if (!state.customizations[imageIndex][`${elementType}Spacing`]) {
                state.customizations[imageIndex][`${elementType}Spacing`] = getDefaultCustomization()[`${elementType}Spacing`];
            }
            state.customizations[imageIndex][`${elementType}Spacing`].lineHeight = lineHeight;
        }
    }
});

export const {
    setCustomizationPosition,
    setCustomizationData,
    setDragging,
    setResizing,
    setSelectedElement,
    setElementRotation,
    setElementOpacity,
    resizeElement,
    setCurrentProduct,
    setCurrentPosition,
    setFontFamily,
    setTextStyle,
    setNameColor,
    setNumberColor,
    setTextEffects,
    setCurveSettings,
    setAlignment,
    setSpacing,
    setBlendMode,
    setElementLock,
    setElementFilter,
    setElementBorder,
    setTextCurve,
    setTextShadow,
    setTextAlignment,
    setLetterSpacing,
    setLineHeight,
    setUploadedFile,
    setPreviewImage,
    clearUploadedFile,
    initializeImageCustomization,
    toggleCustomizationVisibility,
    resetCustomizations,
    resetImageCustomization,
    resetProductCustomizations,
    saveCustomizationsFromCustomizePage,
    setLogoSize,
    saveToHistory,
    undo,
    redo,
    clearHistory,
    setNameStyles,
    setNumberStyles,
    setLogoStyles
} = customizationSlice.actions;

export default customizationSlice.reducer;